import { useState, useEffect, useRef, useCallback } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, isCardTransparent, getAnimation, hexToRgba } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

function AnimatedNumber({ value, visible, duration = 2000 }) {
    const [display, setDisplay] = useState(0);
    const num = parseFloat(String(value).replace(/,/g, '')) || 0;
    const isFloat = num % 1 !== 0;
    const rafRef = useRef(null);

    useEffect(() => {
        if (!visible || num === 0) { setDisplay(0); return; }
        const start = performance.now();
        const tick = (now) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            // ease-out: 1 - (1 - t)^3
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = eased * num;
            setDisplay(isFloat ? parseFloat(current.toFixed(1)) : Math.floor(current));
            if (progress < 1) rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    }, [visible, num, duration]);

    return <span>{visible ? display.toLocaleString() : '0'}</span>;
}

export default function NumberTickerSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const items = content.items || [];
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const variant = config.variant || 'large';
    const columns = config.columns || 4;
    const duration = config.duration || 2000;

    if (items.length === 0) return null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };
    const cols = columns === 2 ? 'grid-cols-2' : columns === 3 ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-4';

    // ── Cards variant ──
    if (variant === 'cards') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid ${cols} gap-6`}>
                        {items.map((item, i) => (
                            <div key={i} className={`${card} p-8 text-center transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${200 + i * 100}ms` : '0ms' }}>
                                {item.icon && <div className="text-3xl mb-3">{item.icon}</div>}
                                <div className="text-4xl md:text-5xl font-extrabold mb-2" style={{ color: s.accent }}>
                                    {item.prefix}<AnimatedNumber value={item.number} visible={visible} duration={duration} />{item.suffix}
                                </div>
                                <p className="text-sm font-medium" style={{ color: s.text }}>{item.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Compact variant ──
    if (variant === 'compact') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="flex flex-wrap items-center justify-center divide-x" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.12)' : hexToRgba(s.accent, 0.13) }}>
                        {items.map((item, i) => (
                            <div key={i} className={`text-center px-8 md:px-12 py-4 transition-all duration-500 ${visible ? 'opacity-100' : 'opacity-0'}`}
                                style={{ transitionDelay: visible ? `${200 + i * 100}ms` : '0ms' }}>
                                {item.icon && <div className="text-xl mb-1">{item.icon}</div>}
                                <div className="text-3xl md:text-4xl font-extrabold" style={{ color: s.accent }}>
                                    {item.prefix}<AnimatedNumber value={item.number} visible={visible} duration={duration} />{item.suffix}
                                </div>
                                <p className="text-sm mt-1" style={{ color: s.textSub }}>{item.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Large variant (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <div className={`grid ${cols} gap-8 md:gap-12`}>
                    {items.map((item, i) => (
                        <div key={i} className={`text-center transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                            style={{ transitionDelay: visible ? `${200 + i * 150}ms` : '0ms' }}>
                            {item.icon && <div className="text-4xl mb-4">{item.icon}</div>}
                            <div className="text-5xl md:text-6xl lg:text-7xl font-extrabold mb-3 tracking-tight" style={{ color: s.accent }}>
                                {item.prefix}<AnimatedNumber value={item.number} visible={visible} duration={duration} />{item.suffix}
                            </div>
                            <p className="text-sm md:text-base font-medium" style={{ color: s.text }}>{item.label}</p>
                        </div>
                    ))}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
